import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessNotification from '../../components/SuccessNotification';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import '../ViewDataPage/styles.css';
import SidebarLayout from '../../components/SidebarLayout';
import styles from '../../components/ViewDataContent.module.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const DenseAdminRow = memo(({ index, style, data }) => {
  const { rows, columns, selectedIds, toggleSelectOne, gridTemplateColumns } = data;
  const record = rows[index];
  if (!record) return null;

  const isSelected = selectedIds.has(record.record_id);
  const rowClass = [
    styles.tableRow,
    index % 2 === 0 ? styles.rowEven : styles.rowOdd,
    isSelected ? styles.rowSelected : ''
  ]
    .filter(Boolean)
    .join(' ');

  const resolveValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  };

  return (
    <div
      style={{ ...style, gridTemplateColumns }}
      className={rowClass}
      role="row"
      aria-selected={isSelected}
    >
      {columns.map((column) => {
        if (column.id === '__select') {
          return (
            <div key={column.id} className={`${styles.cell} ${styles.checkboxCell}`} role="gridcell">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelectOne(record.record_id)}
                aria-label={`Select row ${index + 1}`}
              />
            </div>
          );
        }

        const value = resolveValue(record.record_data?.[column.id]);
        return (
          <div key={column.id} className={styles.cell} role="gridcell" title={value}>
            {value}
          </div>
        );
      })}
    </div>
  );
});

const AdminViewDataPage = () => {
  const navigate = useNavigate();
  const { id: entityId, schemaId } = useParams();
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartType, setChartType] = useState('bar');
  const [xField, setXField] = useState('');
  const [yField, setYField] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    if (schemaId) {
      setLoading(true);
      Promise.all([fetchSchema(), fetchData()]).finally(() => setLoading(false));
    }
  }, [schemaId]);

  // Skeleton-only refresh when toggling includeDeleted or changing page
  useEffect(() => {
    if (!schemaId) return;
    setRefreshing(true);
    fetchData().finally(() => setRefreshing(false));
  }, [includeDeleted, page, schemaId]);

  const fetchSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint with entity_id query parameter
      const url = `${API_URL}/admin/schemas/${schemaId}?entity_id=${entityId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // console.log("schema response", response);

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const result = await response.json();
      setSchema(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint with entity_id
      const url = `${API_URL}/admin/entities/${entityId}/data?schema_id=${schemaId}${includeDeleted ? '&include_deleted=true' : ''}&limit=${pageSize}&page=${page}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      setData(result.data?.records || []);
      if (result.data?.totalRecords != null) setTotalRecords(result.data.totalRecords);
      if (result.data?.page != null) setPage(result.data.page);
      if (result.data?.pageSize != null) setPageSize(result.data.pageSize);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectAllEnabled = data.length > 0 && selectedIds.size === data.length;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === data.length) {
        return new Set();
      }
      return new Set((data || []).map((record) => record.record_id));
    });
  }, [data]);

  const toggleSelectOne = useCallback((recordId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  }, []);

  const handleDeleteClick = () => {
    if (selectedIds.size === 0) return;
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setShowConfirmModal(false);
    setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API_URL}/admin/entities/${entityId}/data/delete`;
      const body = { schema_id: schemaId, record_ids: Array.from(selectedIds) };
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error(await resp.text() || 'Failed to delete records');
      
      const result = await resp.json();
      const deletedCount = result.deletedCount ?? result.deleted ?? selectedIds.size;
      const failedIds = result.failedIds || [];
      
      // Show success message
      if (failedIds.length > 0) {
        setSuccessMessage(`Deleted ${deletedCount}, failed ${failedIds.length}: ${failedIds.slice(0,5).join(', ')}${failedIds.length>5?'...':''}`);
      } else {
        setSuccessMessage(`Successfully deleted ${deletedCount} ${deletedCount === 1 ? 'record' : 'records'}`);
      }
      setShowSuccess(true);
      
      // Refresh data after a short delay to show skeleton
      setTimeout(async () => {
        await fetchData();
        setRefreshing(false);
      }, 800);
      
    } catch (err) {
      setError(err.message || 'Failed to delete');
      setRefreshing(false);
    } finally {
      setDeleting(false);
    }
  };

  const dataColumns = useMemo(() => {
    if (data.length === 0) return [];
    const firstRecord = data[0];
    if (firstRecord?.record_data) {
      return Object.keys(firstRecord.record_data);
    }
    return [];
  }, [data]);

  const tableColumnsStructure = useMemo(() => {
    const computed = dataColumns.map((column) => {
      const approxLength = column.length;
      const width = Math.min(260, Math.max(120, approxLength * 12));
      return { id: column, label: column, width };
    });
    return [{ id: '__select', label: '', width: 48 }, ...computed];
  }, [dataColumns]);

  const gridTemplateColumns = useMemo(
    () => tableColumnsStructure.map((column) => `${column.width}px`).join(' '),
    [tableColumnsStructure]
  );

  const tableWidth = useMemo(
    () => tableColumnsStructure.reduce((sum, column) => sum + column.width, 0),
    [tableColumnsStructure]
  );

  const listData = useMemo(
    () => ({
      rows: data,
      columns: tableColumnsStructure,
      selectedIds,
      toggleSelectOne,
      gridTemplateColumns
    }),
    [data, tableColumnsStructure, selectedIds, toggleSelectOne, gridTemplateColumns]
  );

  const itemKey = useCallback(
    (index, itemData) => itemData.rows[index]?.record_id ?? `row-${index}`,
    []
  );

  const prepareChartData = () => {
    if (!xField || !yField) return [];
    return data.map(record => ({
      [xField]: record.record_data[xField],
      [yField]: parseFloat(record.record_data[yField]) || 0
    }));
  };

  const renderChart = () => {
    const chartData = prepareChartData();
    if (chartData.length === 0) return null;

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xField} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey={yField} fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xField} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey={yField} stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey={yField}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="view-data-container">
          <div className="view-data-loading">
            <div className="loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!loading && !schema) {
    return (
      <SidebarLayout>
        <div className="view-data-container">
          <div className="view-data-error">
            <h3>Schema not found</h3>
            <p>The requested table schema could not be found.</p>
            <button 
              onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
              className="back-to-tables-btn"
            >
              Back to Tables
            </button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  const columns = dataColumns;

  return (
    <SidebarLayout>
      <div className="view-data-container">
        <div className="view-data-header">
          <div>
            <h1>{schema?.table_name}</h1>
            <p>View and analyze school data</p>
            <div className="view-data-info">
              <span className="record-count">{data.length} records</span>
              <span className="column-count">{columns.length} columns</span>
            </div>
          </div>
          <div className="view-data-actions">
            <button 
              onClick={() => navigate(`/admin/entities/${entityId}/add-data/${schemaId}`)}
              className="add-data-btn"
            >
              + Add Data
            </button>
            <button 
              onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
              className="view-data-back-btn"
            >
              Back to Tables
            </button>
          </div>
        </div>

        {error && (
          <div className="view-data-error">
            {error}
          </div>
        )}

        <div className="view-data-content">


          <div className="view-data-section">
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <div>
                  <p className={styles.kicker}>School Dataset</p>
                  <h2 className={styles.title}>{schema?.table_name || 'Dataset'}</h2>
                  <p className={styles.caption}>
                    {totalRecords > 0
                      ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalRecords)} of ${totalRecords}`
                      : `${data.length} records`}
                  </p>
                </div>
                <div className={styles.tableStats}>
                  <span className={styles.statBadge}>{data.length} records</span>
                  <span className={styles.statBadge}>{columns.length} columns</span>
                </div>
              </div>

              <div className={styles.tableToolbar}>
                <div className={styles.tableToolbarLeft}>
                  <span className={styles.toolbarHint}>
                    {totalRecords > 0 ? `Page ${page} · ${pageSize} per page` : 'All rows loaded'}
                  </span>
                  <label className={styles.toggleDeleted}>
                    <input
                      type="checkbox"
                      checked={includeDeleted}
                      onChange={() => setIncludeDeleted((prev) => !prev)}
                    />
                    Show deleted
                  </label>
                </div>
                <div className={styles.tableToolbarRight}>
                  <button
                    className="danger-btn"
                    onClick={handleDeleteClick}
                    disabled={selectedIds.size === 0 || deleting || refreshing}
                    title={selectedIds.size === 0 ? 'Select rows to delete' : 'Delete selected rows'}
                  >
                    {deleting ? 'Deleting…' : `Delete Selected (${selectedIds.size})`}
                  </button>
                  <div className={styles.paginationGroup}>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || refreshing || deleting}
                    >
                      Prev
                    </button>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setPage((p) => (p * pageSize < totalRecords ? p + 1 : p))}
                      disabled={page * pageSize >= totalRecords || refreshing || deleting}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {data.length > 0 ? (
                <div className={styles.tableSurface}>
                  <div
                    className={styles.tableHeaderRow}
                    role="row"
                    style={{ gridTemplateColumns }}
                  >
                    {tableColumnsStructure.map((column) => (
                      <div
                        key={column.id}
                        className={styles.columnHeader}
                        style={{ width: column.width }}
                        role="columnheader"
                      >
                        {column.id === '__select' ? (
                          <input
                            type="checkbox"
                            checked={selectAllEnabled}
                            onChange={toggleSelectAll}
                            aria-label="Select all rows"
                          />
                        ) : (
                          column.label
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.virtualWrapper}>
                    <div className={styles.horizontalScroll}>
                      <div className={styles.tableBodySurface} style={{ minWidth: tableWidth }}>
                        <AutoSizer disableWidth>
                          {({ height }) => (
                            <List
                              height={height}
                              width={tableWidth}
                              itemCount={data.length}
                              itemSize={36}
                              itemData={listData}
                              itemKey={itemKey}
                              className={styles.virtualList}
                              overscanCount={18}
                            >
                              {DenseAdminRow}
                            </List>
                          )}
                        </AutoSizer>
                      </div>
                    </div>
                    {(loading || refreshing) && (
                      <div className={styles.refreshOverlay}>
                        {Array.from({ length: 6 }).map((_, idx) => (
                          <div
                            key={`refresh-${idx}`}
                            className={`${styles.tableRow} ${styles.skeletonRow}`}
                            style={{ gridTemplateColumns }}
                          >
                            {tableColumnsStructure.map((column) => (
                              <div key={`${column.id}-${idx}`} className={styles.skeletonCell}>
                                <span className={styles.shimmer} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <p className={styles.emptyTitle}>No records yet</p>
                  <p className={styles.emptyText}>
                    Add or upload data for this school to see records populate in real time.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Records"
          message={`Are you sure you want to delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'record' : 'records'}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          icon="🗑️"
          loading={deleting}
        />

        {/* Success Notification */}
        <SuccessNotification
          isVisible={showSuccess}
          message={successMessage}
          onClose={() => setShowSuccess(false)}
          duration={4000}
        />
      </div>
    </SidebarLayout>
  );
};

export default AdminViewDataPage;

